/**
 * Gestion unifiee du clavier virtuel pour la page Assistant (chat).
 * Fonctionne a la fois :
 *   - en PWA / navigateur mobile  -> via l'API window.visualViewport
 *   - dans l'app native Capacitor -> via les evenements du plugin @capacitor/keyboard
 *
 * Quand le clavier est ouvert on pose sur <html> :
 *   - l'attribut data-keyboard-open="true"
 *   - la variable --kb-visible-height : hauteur EXACTE de la zone visible au-dessus du clavier
 *   - la variable --kb-top : decalage haut de la zone visible (iOS quand la page scrolle)
 * Le CSS epingle alors .chat-container dans cette zone (barre de saisie collee au clavier)
 * et masque la barre de navigation du bas.
 */
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

const applyOpen = (visibleHeight, top) => {
  const root = document.documentElement;
  root.style.setProperty('--kb-visible-height', `${Math.round(visibleHeight)}px`);
  root.style.setProperty('--kb-top', `${Math.round(top || 0)}px`);
  root.setAttribute('data-keyboard-open', 'true');
};

const applyClose = () => {
  const root = document.documentElement;
  root.setAttribute('data-keyboard-open', 'false');
  root.style.removeProperty('--kb-visible-height');
  root.style.removeProperty('--kb-top');
};

export function useChatKeyboard() {
  useEffect(() => {
    // --- App native (Capacitor) : plugin clavier, WebView NON redimensionnee (resize: none) ---
    if (Capacitor?.isNativePlatform?.()) {
      const handles = [];
      let active = true;
      import('@capacitor/keyboard')
        .then(({ Keyboard }) => {
          if (!active) return;
          Keyboard.addListener('keyboardWillShow', (info) => {
            const kb = info?.keyboardHeight || 0;
            applyOpen(window.innerHeight - kb, 0);
          }).then((h) => handles.push(h));
          Keyboard.addListener('keyboardWillHide', () => applyClose()).then((h) => handles.push(h));
        })
        .catch((err) => console.info('[Mibeko] Keyboard plugin skipped:', err?.message || err));
      return () => {
        active = false;
        handles.forEach((h) => h?.remove?.());
        applyClose();
      };
    }

    // --- PWA / navigateur : visualViewport ---
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onChange = () => {
      const kb = window.innerHeight - vv.height - vv.offsetTop;
      if (kb > 120) applyOpen(vv.height, vv.offsetTop);
      else applyClose();
    };
    vv.addEventListener('resize', onChange);
    vv.addEventListener('scroll', onChange);
    onChange();
    return () => {
      vv.removeEventListener('resize', onChange);
      vv.removeEventListener('scroll', onChange);
      applyClose();
    };
  }, []);
}
